import { CalendarClock, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { listOnCallShifts, type OnCallShiftRow } from '../api';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';
import { formatAbsoluteDate, formatRelativeDate } from '../utils/formatters';

type OnCallSchedulesPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function OnCallSchedulesPage({ openDialog }: OnCallSchedulesPageProps) {
  const { session } = useSession();
  const { workspace, activeOrganization, workspaceVersion } = useWorkspace();
  const { onCallSchedules } = workspace;

  const [expandedScheduleId, setExpandedScheduleId] = useState('');
  const [shiftsByScheduleId, setShiftsByScheduleId] = useState<Record<string, OnCallShiftRow[]>>({});
  const [shiftsLoading, setShiftsLoading] = useState(false);

  const loadShifts = useCallback(async (onCallScheduleId: string) => {
    if (!session) return;
    setShiftsLoading(true);
    try {
      const shifts = await listOnCallShifts(session.accessToken, onCallScheduleId);
      setShiftsByScheduleId((previous) => ({ ...previous, [onCallScheduleId]: shifts }));
    } catch {
      setShiftsByScheduleId((previous) => ({ ...previous, [onCallScheduleId]: [] }));
    } finally {
      setShiftsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (expandedScheduleId) {
      void loadShifts(expandedScheduleId);
    }
  }, [expandedScheduleId, workspaceVersion, loadShifts]);

  function handleCreateSchedule() {
    if (!activeOrganization) return;
    openDialog({
      kind: 'oncall-schedule',
      organizationId: activeOrganization.id,
      teamHandle: '',
      name: '',
      rotationType: 'weekly',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    });
  }

  function handleAddShift(schedule: (typeof onCallSchedules)[number]) {
    openDialog({
      kind: 'oncall-shift',
      onCallScheduleId: schedule.onCallScheduleId,
      scheduleName: schedule.name,
      workIdentityId: '',
      startsAt: '',
      endsAt: ''
    });
  }

  return (
    <SectionCard
      label="On-call"
      title="Schedules"
      description="Rotation schedules that determine who is on call for a team."
      action={
        <button type="button" className="primaryButton" onClick={handleCreateSchedule}>
          <Plus size={14} />
          <span>New schedule</span>
        </button>
      }
    >
      {onCallSchedules.length > 0 ? (
        <div className="releaseTableWrap" role="region" aria-label="On-call schedules table" tabIndex={0}>
          <table className="releaseTable">
            <thead>
              <tr>
                <th aria-label="Expand" />
                <th>Name</th>
                <th>Team</th>
                <th>Rotation</th>
                <th>Timezone</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {onCallSchedules.map((schedule) => {
                const expanded = expandedScheduleId === schedule.onCallScheduleId;
                const shifts = shiftsByScheduleId[schedule.onCallScheduleId] || [];
                return (
                  <React.Fragment key={schedule.onCallScheduleId}>
                    <tr>
                      <td>
                        <button
                          type="button"
                          className="iconButton"
                          aria-label={expanded ? 'Collapse shifts' : 'Expand shifts'}
                          onClick={() => setExpandedScheduleId(expanded ? '' : schedule.onCallScheduleId)}
                        >
                          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </td>
                      <td><strong>{schedule.name}</strong></td>
                      <td>{schedule.teamHandle}</td>
                      <td>{schedule.rotationType}</td>
                      <td>{schedule.timezone}</td>
                      <td className="releaseTableAction">
                        <button type="button" className="secondaryButton" onClick={() => handleAddShift(schedule)}>
                          Add shift
                        </button>
                      </td>
                    </tr>
                    {expanded ? (
                      <tr>
                        <td />
                        <td colSpan={5}>
                          {shiftsLoading && shifts.length === 0 ? (
                            <div style={{ padding: '8px 0', color: 'var(--text-faint)', fontSize: 13 }}>
                              Loading shifts…
                            </div>
                          ) : shifts.length === 0 ? (
                            <EmptyState
                              compact
                              icon={<CalendarClock size={16} />}
                              title="No shifts yet"
                              description="Add a shift to assign someone to this schedule."
                            />
                          ) : (
                            <table className="releaseTable">
                              <thead>
                                <tr>
                                  <th>Work identity</th>
                                  <th>Starts</th>
                                  <th>Ends</th>
                                  <th>Override</th>
                                </tr>
                              </thead>
                              <tbody>
                                {shifts.map((shift) => (
                                  <tr key={shift.onCallShiftId}>
                                    <td><code>{shift.workIdentityId}</code></td>
                                    <td title={formatAbsoluteDate(shift.startsAt)}>{formatRelativeDate(shift.startsAt)}</td>
                                    <td title={formatAbsoluteDate(shift.endsAt)}>{formatRelativeDate(shift.endsAt)}</td>
                                    <td>{shift.isOverride ? 'Yes' : 'No'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<CalendarClock size={20} />}
          title="No on-call schedules yet"
          description="Create a schedule to define who is on point for a team."
          action={
            <button type="button" className="primaryButton" onClick={handleCreateSchedule}>
              <Plus size={14} />
              <span>New schedule</span>
            </button>
          }
        />
      )}
    </SectionCard>
  );
}

export { OnCallSchedulesPage };
